<template>
  <form-field
    label="Agency Name (Required)"
    :auto-focus="true"
    :value="agency.name"
    placeholder="Enter agency name"
    :on-input="onInput"
    name="name"
  />

  <form-field
    ref="insurancePhoneField"
    label="Agency Phone # (Required)"
    :value="agency.phone_number"
    placeholder="Enter agency phone"
    :on-input="onInput"
    name="phone_number"
    type="tel"
    :on-blur="validateCorrectPhone"
    :error="phoneError"
    pattern="number"
  />

  <form-field
    label="Email (Required)"
    :value="agency.email"
    placeholder="Enter agency email"
    :on-input="onInput"
    type="email"
    name="email"
    :error="emailError"
  />
</template>

<script>
import { mapState } from 'vuex';

import { formatPhone, isEmail } from '@/common/validators';
import FormField from '@/components/FormField.vue';

// Mixin
import phoneField from '@/mixins/phoneField';

export default {
  name: 'InsuranceForm',
  components: {
    FormField,
  },
  mixins: [phoneField],
  props: {
    name: {
      type: String,
      required: true,
    },
    stepName: {
      type: String,
      required: true,
    },
    sourceStepName: {
      type: String,
      required: true,
    },
    updateField: {
      type: Function,
      required: true,
    },
    data: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      agency: {
        name: '',
        phone_number: '',
        email: '',
        emailError: '',
      },
      input_phone_number_with_country_code: null,
      emailError: '',
    };
  },
  computed: {
    ...mapState({
      isEmailRequiredForLogin: (state) =>
        state.carriers?.isEmailRequiredForLogin || false,
      currentUserEmail: (state) => state.users.currentUser?.email || '',
      currentUserPhone: (state) => state.users.currentUser?.phone_e164 || '',
    }),
  },
  mounted() {
    const inputs = document.getElementsByClassName('iti__selected-flag');
    if (inputs.length > 0) {
      inputs?.[0].setAttribute('tabindex', '-1');
    }
  },
  methods: {
    isSamePhoneAsTheCurrentUser() {
      // Only enter this option if the user has not configured a telephone - Only for cases of large carriers
      if (!this.currentUserPhone) return false;

      const find = '-';
      const re = new RegExp(find, 'g');

      const phoneAgency = this.agency.phone_number.replace(re, '');
      const phoneWithoutCountryCode = this.currentUserPhone.substr(
        this.currentUserPhone.length - 10
      );
      if (phoneWithoutCountryCode == phoneAgency) {
        this.phoneError =
          'Your certificate must be provided by an insurance agency';
        return true;
      }
      return false;
    },
    isSameEmailAsTheCurrentUser() {
      if (this.currentUserEmail == this.agency.email) {
        this.emailError =
          'Your certificate must be provided by an insurance agency';
        return true;
      }
      return false;
    },
    validateCorrectPhone(e) {
      if (
        ['us', 'mx', 'ca'].includes(
          this.$refs?.insurancePhoneField?.elementData?.selectedCountryData
            ?.iso2
        )
      ) {
        this.validatePhone(e);
      } else {
        this.validateEuroPhone(
          e,
          this.$refs?.insurancePhoneField?.elementData?.isValidNumber()
        );
        if (this.isSamePhoneAsTheCurrentUser())
          this.phoneError = 'Cannot use your own phone number';
      }
    },
    validatePhoneAndEquality(e) {
      this.validatePhone(e);
      if (!this.isEmailRequiredForLogin) this.isSamePhoneAsTheCurrentUser();
    },
    async onInput(e) {
      if (e.target.name.includes('phone_number')) {
        let regex = /^(\d|-)+$/;
        // Validates that the phone only contains numbers or hyphens
        if (!regex.test(e.target.value)) {
          this.validatePhone(e);
          if (this.phoneError)
            this.$store.commit('toggleDisabledButton', {
              bool: true,
              source: 'InsuranceForm #1',
            });
        }
        const value = formatPhone(e.target.value);
        this.input_phone_number_with_country_code =
          this.$refs?.insurancePhoneField?.elementData?.getNumber();
        this.agency[e.target.name] = value;
        if (
          ['us', 'mx', 'ca'].includes(
            this.$refs?.insurancePhoneField?.elementData?.selectedCountryData
              ?.iso2
          )
        ) {
          if (this.agency.phone_number.length === 12) {
            this.validatePhone(e);

            if (this.isSamePhoneAsTheCurrentUser())
              this.$store.commit('toggleDisabledButton', {
                bool: true,
                source: 'InsuranceForm #2',
              });
          }
        } else {
          this.validateEuroPhone(
            e,
            this.$refs?.insurancePhoneField?.elementData?.isValidNumber()
          );
          if (this.$refs?.insurancePhoneField?.elementData?.isValidNumber()) {
            if (this.isSamePhoneAsTheCurrentUser())
              this.$store.commit('toggleDisabledButton', {
                bool: true,
                source: 'InsuranceForm #2',
              });
          }
        }
      } else {
        this.agency[e.target.name] = e.target.value;
      }

      if (isEmail(this.agency.email)) {
        this.emailError = '';
        if (this.isSameEmailAsTheCurrentUser())
          this.$store.commit('toggleDisabledButton', {
            bool: true,
            source: 'InsuranceForm #3',
          });
        if (!this.isEmailRequiredForLogin && this.isSamePhoneAsTheCurrentUser())
          this.$store.commit('toggleDisabledButton', {
            bool: true,
            source: 'InsuranceForm #4',
          });
        if (
          !this.emailError &&
          !this.phoneError &&
          this.agency.name &&
          this.agency.phone_number &&
          this.agency.email
        ) {
          if (
            ['us', 'mx', 'ca'].includes(
              this.$refs?.insurancePhoneField?.elementData?.selectedCountryData
                ?.iso2
            )
          ) {
            if (this.agency.phone_number.length === 12) {
              await this.addAgency(this.agency);
            }
          } else {
            if (this.$refs?.insurancePhoneField?.elementData?.isValidNumber()) {
              await this.addAgency(this.agency);
            }
          }
        }
      } else {
        this.$store.commit('toggleDisabledButton', {
          bool: true,
          source: 'InsuranceForm #5',
        });
      }
    },
    async addAgency(item) {
      item.input_phone_number_with_country_code =
        this.input_phone_number_with_country_code;
      const agencyObject = {
        ...this.data[this.name],
        agency: item,
      };

      this.updateField(this.name, agencyObject);
      await this.$store.dispatch('onboardingPacket/setOnboardingBody', {
        insurance_certificate_request: {
          contact_name: item.name,
          contact_phone:
            this.input_phone_number_with_country_code || item.phone_number,
          contact_email: item.email,
        },
      });
      if (this.stepName == 'Insurance-3') return;
      this.$store.commit('toggleDisabledButton', {
        bool: false,
        source: 'InsuranceForm #6',
      });
    },
  },
};
</script>
